class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    this.checkAndHideCrossSell();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    if (!this.classList.contains('is-empty')) {
      // updateThreshold(); 已不再使用 JavaScript 更新
    }
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  checkAndHideCrossSell() {
    const cross_sell_variant = document.querySelector('.cross_sell_variant_id');
    if (!cross_sell_variant) {
      return;
    }
    const cart_items = [];
    fetch('/cart.js')
      .then((response) => response.json())
      .then((data) => {
        data.items.forEach((item) => {
          cart_items.push(parseInt(item.id));
        });
      })
      .finally(() => {
        //console.log("cart-drawer.js: fetch completed", cart_items);
        const cross_sell_wrapper = document.getElementById('cross_sell');
        const cross_sell_id = parseInt(cross_sell_variant.value);
        if (!cart_items.includes(cross_sell_id)) {
          cross_sell_wrapper.style.display = 'block';
        }
      });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

async function requestCartUpdate(url, body) {
  const response = await fetch(url, {
    ...fetchConfig(),
    body: JSON.stringify(body),
  });
  const state = await response.json();

  if (!response.ok || state.status) {
    throw new Error(state.description || state.message || 'Unable to update cart.');
  }

  return state;
}

window.syncCartProtection = async (cart, sections = []) => {
  const config = window.cartProtectionConfig;
  if (!config || !config.variantId) return cart;

  const isProtectionItem = (item) => Number(item.variant_id) === Number(config.variantId);
  const protectionItem = cart.items.find(isProtectionItem);
  const hasRegularItems = cart.items.some((item) => !isProtectionItem(item));
  const isDisabled = cart.attributes && cart.attributes._shipping_protection_disabled === 'true';
  const sectionData = {
    sections,
    sections_url: window.location.pathname,
  };

  if (!hasRegularItems && protectionItem) {
    return requestCartUpdate('/cart/change.js', {
      ...sectionData,
      id: protectionItem.key,
      quantity: 0,
    });
  }

  if (isDisabled && protectionItem) {
    return requestCartUpdate('/cart/change.js', {
      ...sectionData,
      id: protectionItem.key,
      quantity: 0,
    });
  }

  if (!hasRegularItems || isDisabled) return cart;

  if (!protectionItem) {
    return requestCartUpdate('/cart/add.js', {
      ...sectionData,
      items: [{ id: Number(config.variantId), quantity: 1 }],
    });
  }

  if (protectionItem.quantity !== 1) {
    return requestCartUpdate('/cart/change.js', {
      ...sectionData,
      id: protectionItem.key,
      quantity: 1,
    });
  }

  return cart;
};

class CartProtectionToggle extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-protection-toggle]');
    this.status = this.querySelector('[data-protection-status]');
    if (!this.toggle) return;

    this.toggle.addEventListener('change', () => this.updateProtection());
    this.syncProtection();
  }

  async syncProtection() {
    if (this.updating) return;

    try {
      const cart = await fetch('/cart.js').then((response) => response.json());
      const state = await window.syncCartProtection(cart, ['cart-drawer', 'cart-icon-bubble']);
      const cartDrawer = document.querySelector('cart-drawer');

      if (state !== cart && cartDrawer && state.sections) {
        cartDrawer.renderContents(state);
      }
    } catch (error) {
      console.error(error);
    }
  }

  async updateProtection() {
    if (this.updating) return;

    const enabled = this.toggle.checked;
    this.updating = true;
    this.toggle.disabled = true;
    this.status.textContent = '';

    try {
      const sections = ['cart-drawer', 'cart-icon-bubble'];
      const preferenceState = await requestCartUpdate('/cart/update.js', {
        attributes: {
          _shipping_protection_disabled: enabled ? '' : 'true',
        },
        sections,
        sections_url: window.location.pathname,
      });
      const state = await window.syncCartProtection(preferenceState, sections);

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && state.sections) {
        cartDrawer.renderContents(state);
      }
    } catch (error) {
      this.toggle.checked = !enabled;
      this.status.textContent = 'Unable to update protection.';
      console.error(error);
    } finally {
      this.updating = false;
      this.toggle.disabled = false;
    }
  }
}

customElements.define('cart-protection-toggle', CartProtectionToggle);
